import React, { useEffect, useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import Button, { ButtonGroup } from '@atlaskit/button';
import Form, { FormFooter } from '@atlaskit/form';
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { visuallyHiddenStyles } from '@atlassian/jira-accessibility';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	FireScreenAnalytics,
	ContextualAnalyticsData,
	SCREEN,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import GenericErrorNoResults from '../../common/assets/GenericErrorNoResults.svg';
import type { SupportedThirdParty } from '../../common/constants/import-type';
import { X2C_SCREENS } from '../../common/constants/metrics';
import { PageDescription, PageHeading, ErrorHeading } from '../../common/ui';
import { CloseAndReturnButton } from '../../common/ui/close-and-return-button';
import { AsyncMappingTable } from '../../common/ui/mapping-table';
import { StepFooter, StepFooterStandard } from '../../common/ui/step-footer';
import { ProgressBarStandard, StepProgressBar } from '../../common/ui/step-progress-bar';
import { useProjectSpecificContent } from '../../common/utils/project-specific-content';
import { useProjectData, useThirdPartyState } from '../../controllers/import-state';
import { useAccessTokenModalState } from '../../controllers/ui-state';
import { useProductSteps } from '../../controllers/utils';
import { useThirdPartyMembers } from '../../services/third-party-members';
import messages from './messages';
import { type FormData, convertFormDataToUserEmails, useTableHead, useTableRows } from './utils';

type Props = {
	thirdParty: SupportedThirdParty;
	hasProgressBar: boolean;
	onNext: () => void;
	onReturn: () => void;
	onCloseAndReturnToUploadStep: () => void;
};
export const UserMappingScreen = ({
	thirdParty,
	hasProgressBar,
	onReturn,
	onNext,
	onCloseAndReturnToUploadStep,
}: Props) => {
	const [, { setRequestAuthenticationToken }] = useAccessTokenModalState();
	const { fetchThirdPartyMembers, isLoading, hasErrorOccurred } = useThirdPartyMembers({
		thirdParty,
		requestAuthenticationToken: setRequestAuthenticationToken,
	});
	const [thirdPartyState] = useThirdPartyState();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ members, userEmails }, { setThirdPartyState }] = useThirdPartyState();
	const { productSteps, currentStepIndex } = useProductSteps();
	const { formatMessage } = useIntl();
	const [{ type }] = useProjectData();
	const { userMappingScreenDescription } = useProjectSpecificContent(type);
	const { isSiteAdmin, isAdmin } = useTenantContext();

	const onFetchThirdPartyMembers = useCallback(() => {
		thirdPartyState.selectedEntityId &&
			fetchThirdPartyMembers(thirdPartyState.selectedEntityId).then((data) =>
				setThirdPartyState({ members: data.members, userEmails: data.userEmails }),
			);
	}, [fetchThirdPartyMembers, setThirdPartyState, thirdPartyState.selectedEntityId]);

	useEffect(() => {
		!members && onFetchThirdPartyMembers();
	}, [
		fetchThirdPartyMembers,
		members,
		onFetchThirdPartyMembers,
		setThirdPartyState,
		thirdParty,
		thirdPartyState.selectedEntityId,
	]);
	const tableHead = useTableHead();
	const tableRows = useTableRows(members, userEmails);

	const handleSubmit = useCallback(
		(formData: FormData) => {
			const userEmailsFromForm = convertFormDataToUserEmails(formData);
			setThirdPartyState({ userEmails: userEmailsFromForm });

			fireUIAnalytics(createAnalyticsEvent({}), 'thirdPartyMembersEmails entered', {
				thirdParty,
				numTotalMembers: members?.length || 0,
				numMembersWithEmail: Object.keys(userEmailsFromForm).length,
			});

			onNext();
		},
		[createAnalyticsEvent, members?.length, onNext, setThirdPartyState, thirdParty],
	);

	const renderStepFooterComponent = useMemo(() => {
		if (fg('enable-x2c-standardised-flow')) {
			return (
				<StepFooterStandard
					alignRight
					onReturn={onReturn}
					isLoading={isLoading}
					nextButtonType="submit"
				/>
			);
		}
		return (
			<FormFooter align="end">
				<StepFooter onReturn={onReturn} isLoading={isLoading} nextButtonType="submit" />
			</FormFooter>
		);
	}, [isLoading, onReturn]);

	const getContent = useCallback(() => {
		if (hasErrorOccurred) {
			return (
				<ErrorWrapper>
					<Illustration
						src={GenericErrorNoResults}
						aria-describedby="user-error-icon"
						role="img"
						alt=""
					/>
					<ErrorHeading id="user-error-icon">{formatMessage(messages.error)}</ErrorHeading>

					{fg('enable-x2c-standardised-flow') ? (
						<>
							<ButtonGroup>
								<Button appearance="primary" onClick={onFetchThirdPartyMembers}>
									{formatMessage(messages.tryAgain)}
								</Button>
							</ButtonGroup>
							<StepFooterStandard alignRight onReturn={onReturn} shouldDisableNext />
						</>
					) : (
						<ButtonGroup>
							<Button onClick={onReturn}>{formatMessage(messages.back)}</Button>
							<Button appearance="primary" onClick={onFetchThirdPartyMembers}>
								{formatMessage(messages.tryAgain)}
							</Button>
						</ButtonGroup>
					)}
				</ErrorWrapper>
			);
		}

		return (
			<>
				<PageDescription>{formatMessage(userMappingScreenDescription)}</PageDescription>
				<FormWrapper>
					{ff('enable-product-admin-for-x2c-migration_zmlsv') && !isSiteAdmin && isAdmin && (
						<Box paddingBlockEnd="space.100">
							<SectionMessage
								title={formatMessage(messages.newUserNotAllowedToImportBannerHeading)}
							>
								<Box as="p">
									{formatMessage(messages.newUserNotAllowedToImportBannerDescription)}
								</Box>
							</SectionMessage>
						</Box>
					)}
					<Form onSubmit={handleSubmit}>
						{({ formProps }) => (
							<StyledForm {...formProps} isLoading={isLoading}>
								<AsyncMappingTable
									head={tableHead}
									rows={tableRows}
									isLoading={isLoading}
									emptyMessage={messages.noUsers}
								/>
								{renderStepFooterComponent}
							</StyledForm>
						)}
					</Form>
				</FormWrapper>
			</>
		);
	}, [
		formatMessage,
		handleSubmit,
		hasErrorOccurred,
		isAdmin,
		isLoading,
		isSiteAdmin,
		onFetchThirdPartyMembers,
		onReturn,
		renderStepFooterComponent,
		tableHead,
		tableRows,
		userMappingScreenDescription,
	]);

	const progressBarComponent = useMemo(() => {
		if (fg('enable-x2c-standardised-flow')) {
			return (
				<>
					{hasProgressBar && (
						<>
							<ProgressBarStandard
								productSteps={productSteps}
								currentStepIndex={currentStepIndex}
								onClose={onCloseAndReturnToUploadStep}
							/>
							<Box xcss={paddingForProgressBar} />
						</>
					)}
				</>
			);
		}
		return (
			<>
				<CloseAndReturnButton onCloseAndReturnToUploadStep={onCloseAndReturnToUploadStep} />
				{hasProgressBar && (
					<StepProgressBar productSteps={productSteps} currentStepIndex={currentStepIndex} />
				)}
			</>
		);
	}, [productSteps, currentStepIndex, hasProgressBar, onCloseAndReturnToUploadStep]);

	return (
		<ContextualAnalyticsData
			sourceName={X2C_SCREENS.USER_MAPPING}
			sourceType={SCREEN}
			attributes={{ thirdParty }}
		>
			<Wrapper data-testid="project-create-importer-upload-flow.ui.user-mapping-screen.wrapper">
				{progressBarComponent}
				<ContentWrapper>
					<PageHeading>{formatMessage(messages.heading)}</PageHeading>
					{getContent()}
				</ContentWrapper>
				<FireScreenAnalytics />
			</Wrapper>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	flexFlow: 'column',
	width: '100%',
	boxSizing: 'border-box',
	padding: `${token('space.300', '24px')} ${token('space.1000', '80px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.input', colors.N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		width: 'fit-content',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	margin: `${token('space.1000', '80px')} auto 0`,
	width: '80%',
	maxWidth: '1024px',
	minHeight: '60%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FormWrapper = styled.div({
	marginTop: token('space.400', '32px'),
	maxWidth: '625px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	label: visuallyHiddenStyles,
});

type StyledFormProps = { isLoading: boolean };

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledForm = styled.form<StyledFormProps>({
	// targeting dynamic table
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> div:first-of-type': {
		maxHeight: 'calc(100vh - 420px)',
		overflowY: 'auto',
		overflowX: 'hidden',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		minHeight: `${(props: StyledFormProps) => (props.isLoading ? '0' : '200px')}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Illustration = styled.img({
	maxWidth: '160px',
	maxHeight: '160px',
	width: '50%',
	height: '50%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	height: '100%',
	flexDirection: 'column',
	justifyContent: 'center',
});

const paddingForProgressBar = xcss({
	paddingTop: 'space.800',
});
